import { isInvalidIxdStructureError } from "@/apis/errors";
import { uploadIxdPreview } from "@/apis/ixd-editor/route";
import { logUserAction } from "@/apis/logs/route";
import { UploadFailedModal } from "@/components/modals/upload-failed-modal";
import queries from "@/queries";
import { pushModal } from "@/stores/modal";
import { Button } from "@/toggle-ui-kit/button";
import { buildEditorViewUrl } from "@/utils/ixd-editor/view-url";
import {
  logHomeServiceAccess,
  logHomeWork,
  logIxdSubmissionUploadRejected,
} from "@/utils/usage-log";
import {
  openPendingTab,
  resolvePendingTab,
  warnPopupBlocked,
} from "@/utils/pending-tab";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { FilePen, Loader2 } from "lucide-react";
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { FileDropZone } from "@/components/file-drop-zone";
import { IxdFileGuide, Notice } from "./notice";

// 집계에서 STEP 카드 로그와 묶이도록 steps.tsx 의 label 과 같게 쓴다
const STEP_LABEL = "IXD 값 입력";

/** STEP5 IXD 값 입력 — 편집용 IXD 를 올려 값 편집기를 새 탭으로 연다. */
export const IxdValueInputPanel = () => {
  const [file, setFile] = useState<File | null>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // 업로드 응답을 기다린 뒤 열면 팝업 차단에 걸리므로 클릭 시점에 연 탭의 주소를 나중에 바꾼다
  const pendingTab = useRef<Window | null>(null);

  const { mutate, isPending } = useMutation({
    mutationFn: uploadIxdPreview,
    onSuccess: ({ reportId }, ixdFile) => {
      logUserAction("IXD 값 편집 시작", [
        { filename: ixdFile.name, ixdReportId: reportId },
      ]);
      // 편집기 화면의 "기존 IXD 선택" 목록에 새 보고서가 보이도록
      queryClient.invalidateQueries({
        queryKey: queries.ixdEditorReports().queryKey,
      });
      const url = buildEditorViewUrl(reportId);
      const tab = pendingTab.current;
      pendingTab.current = null;
      // 업로드는 성공했다 — 탭을 못 열면 같은 탭 이동이라도 줘야 조용히 묻히지 않는다
      if (!resolvePendingTab(tab, url)) {
        warnPopupBlocked("편집 화면 열기", () => navigate(url));
      }
    },
    onError: (error) => {
      pendingTab.current?.close();
      pendingTab.current = null;
      // 제출용 IXD 는 변환 방법을 모달로 안내한다 — 편집기 업로드 화면과 같은 처리
      if (isInvalidIxdStructureError(error)) {
        logIxdSubmissionUploadRejected(`${STEP_LABEL} 패널`);
        pushModal(UploadFailedModal);
      } else
        toast.error("IXD 업로드에 실패했습니다. 잠시 후 다시 시도해주세요.");
    },
  });

  return (
    <>
      <div className="flex w-full flex-col items-end gap-4">
        <Notice>편집할 IXD파일을 업로드해주세요</Notice>
        <FileDropZone
          accept=".ixd"
          hint="*.ixd 형식"
          file={file}
          onFileChange={setFile}
        />
      </div>
      <div className="flex w-full items-start justify-between">
        <Button
          size={40}
          onClick={() => {
            logHomeServiceAccess(`${STEP_LABEL} 패널`, "이전 IXD 불러오기");
            navigate("/ixd-editor?mode=existing-report");
          }}
        >
          이전 IXD 불러오기
        </Button>
        <Button
          variant="solid"
          size={40}
          className="bg-blue-500"
          leftIcon={
            isPending ? (
              <Loader2 className="animate-spin" strokeWidth={1.8} />
            ) : (
              <FilePen strokeWidth={1.8} />
            )
          }
          disabled={!file || isPending}
          onClick={() => {
            if (!file) return;
            logHomeWork(STEP_LABEL, "편집 시작");
            pendingTab.current = openPendingTab({
              title: "IXD 편집 화면 준비 중...",
              message: "IXD 편집 화면을 준비하고 있습니다...",
            });
            mutate(file);
          }}
        >
          편집 시작
        </Button>
      </div>
      <IxdFileGuide />
    </>
  );
};
